interface CustomNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  sound?: string;
  tag?: string;
  renotify?: boolean;
  requireInteraction?: boolean;
  silent?: boolean;
  data?: any;
  actions?: { action: string; title: string; icon?: string }[];
}

interface CheckInNotificationData {
  member: {
    firstName: string;
    lastName: string;
    fullName?: string;
    idNumber: string; // Changed from memberId to idNumber
    profileImage?: string;
  };
  validation: {
    status: 'success' | 'error' | 'warning';
    message: string;
  };
  timestamp: string;
}

class NotificationService {
  private permission: NotificationPermission = 'default';
  private isPageVisible: boolean = true;
  private audio: HTMLAudioElement | null = null;

  constructor() {
    this.initializeService();
  }

  private initializeService() {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('This browser does not support desktop notifications');
      return;
    }

    // Get current permission status
    this.permission = Notification.permission;

    // Listen to page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.isPageVisible = !document.hidden;
    });

    // Initialize notification sound
    this.loadNotificationSound();
  }

  private loadNotificationSound() {
    try {
      // You can add a notification sound file to the public folder
      this.audio = new Audio('/notification.mp3');
      this.audio.volume = 0.3; // Set a reasonable volume
    } catch (error) {
      console.log('Notification sound not available:', error);
    }
  }

  private playNotificationSound() {
    if (this.audio && !this.isPageVisible) {
      this.audio.play().catch(console.error);
    }
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (this.permission === 'default') {
      this.permission = await Notification.requestPermission();
    }

    return this.permission;
  }

  /**
   * Check if notifications are enabled and permitted
   */
  isEnabled(): boolean {
    return 'Notification' in window && this.permission === 'granted';
  }

  /**
   * Show a generic notification
   */
  async showNotification(options: CustomNotificationOptions): Promise<Notification | null> {
    if (!this.isEnabled()) {
      return null;
    }

    try {
      const notificationOptions: NotificationOptions = {
        body: options.body,
        icon: options.icon || '/favicon.ico',
        badge: options.badge || '/favicon.ico',
        tag: options.tag || 'gym-notification',
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        data: options.data
      };

      const notification = new Notification(options.title, notificationOptions);

      // Play sound for important notifications when page is not visible
      if (!this.isPageVisible && !options.silent) {
        this.playNotificationSound();
      }

      // Auto-close after 10 seconds for non-persistent notifications
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 10000);
      }

      return notification;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return null;
    }
  }

  /**
   * Show a check-in specific notification with enhanced details
   */
  async showCheckInNotification(data: CheckInNotificationData): Promise<Notification | null> {
    const memberName = data.member.fullName || `${data.member.firstName} ${data.member.lastName}`;
    const isSuccess = data.validation.status === 'success';
    
    const options: CustomNotificationOptions = {
      title: `🏋️ Member Check-in: ${memberName}`,
      body: isSuccess 
        ? `✅ Access Granted - ${data.validation.message}` 
        : `❌ Access Denied - ${data.validation.message}`,
      icon: data.member.profileImage || '/favicon.ico',
      badge: '/favicon.ico',
      tag: `checkin-${data.member.idNumber}`, // Use idNumber instead of memberId
      renotify: true,
      requireInteraction: !isSuccess, // Persist failure notifications
      silent: false,
      data: {
        type: 'check-in',
        memberId: data.member.idNumber, // Use idNumber instead of memberId
        status: data.validation.status,
        timestamp: data.timestamp
      },
      actions: [
        {
          action: 'view',
          title: 'View Dashboard',
          icon: '/dashboard-icon.png'
        },
        {
          action: 'dismiss',
          title: 'Dismiss',
          icon: '/close-icon.png'
        }
      ]
    };

    const notification = await this.showNotification(options);

    if (notification) {
      // Handle notification click events
      notification.onclick = () => {
        window.focus();
        // Navigate to dashboard or member details
        window.location.hash = '#/dashboard';
        notification.close();
      };
    }

    return notification;
  }

  /**
   * Show membership expiry notification
   */
  async showMembershipExpiryNotification(member: any, daysRemaining: number): Promise<Notification | null> {
    const memberName = member.fullName || `${member.firstName} ${member.lastName}`;
    
    return this.showNotification({
      title: `⚠️ Membership Expiring Soon`,
      body: `${memberName}'s membership expires in ${daysRemaining} day(s)`,
      icon: member.profileImage || '/favicon.ico',
      tag: `expiry-${member.idNumber}`, // Use idNumber instead of memberId
      requireInteraction: true,
      data: {
        type: 'membership-expiry',
        memberId: member.idNumber, // Use idNumber instead of memberId
        daysRemaining
      }
    });
  }

  /**
   * Show payment overdue notification
   */
  async showPaymentOverdueNotification(member: any, amount: number, daysPastDue: number): Promise<Notification | null> {
    const memberName = member.fullName || `${member.firstName} ${member.lastName}`;
    
    return this.showNotification({
      title: `💰 Payment Overdue`,
      body: `${memberName} has an overdue payment of $${amount} (${daysPastDue} days overdue)`,
      icon: member.profileImage || '/favicon.ico',
      tag: `payment-${member.idNumber}`, // Use idNumber instead of memberId
      requireInteraction: true,
      data: {
        type: 'payment-overdue',
        memberId: member.idNumber, // Use idNumber instead of memberId
        amount,
        daysPastDue
      }
    });
  }

  /**
   * Show system notification (new member, updates, etc.)
   */
  async showSystemNotification(title: string, message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): Promise<Notification | null> {
    const icons = {
      info: '💡',
      success: '✅',
      warning: '⚠️',
      error: '❌'
    };

    return this.showNotification({
      title: `${icons[type]} ${title}`,
      body: message,
      tag: 'system-notification',
      requireInteraction: type === 'error',
      data: {
        type: 'system',
        level: type
      }
    });
  }

  /**
   * Clear all notifications with a specific tag
   */
  clearNotifications(tag?: string) {
    // Unfortunately, there's no direct way to clear notifications by tag
    // This is a limitation of the Web Notifications API
    console.log(`Clearing notifications${tag ? ` with tag: ${tag}` : ''}`);
  }

  /**
   * Focus the window and bring it to front (may not work in all browsers due to security)
   */
  focusWindow() {
    try {
      if (window.focus) {
        window.focus();
      }
      
      // Try to bring window to front on Windows
      if (typeof (window as any).external !== 'undefined' && (window as any).external.msIsSiteMode) {
        (window as any).external.msSiteModeActivate();
      }
    } catch (error) {
      console.log('Could not focus window:', error);
    }
  }

  /**
   * Flash the page title for attention
   */
  flashTitle(message: string, duration: number = 5000) {
    const originalTitle = document.title;
    let isFlashing = true;
    
    const flashInterval = setInterval(() => {
      document.title = isFlashing ? `🔔 ${message}` : originalTitle;
      isFlashing = !isFlashing;
    }, 1000);

    setTimeout(() => {
      clearInterval(flashInterval);
      document.title = originalTitle;
    }, duration);
  }

  /**
   * Show an enhanced check-in notification that works even when the tab is not in focus
   */
  async showEnhancedCheckInNotification(data: CheckInNotificationData) {
    const memberName = data.member.fullName || `${data.member.firstName} ${data.member.lastName}`;
    const isSuccess = data.validation.status === 'success';

    // 1. Show browser notification
    await this.showCheckInNotification(data);

    // 2. Flash title if page is visible
    if (this.isPageVisible) {
      this.flashTitle(`${memberName} checked in`);
    }

    // 3. Try to focus window for important events (failures)
    if (!isSuccess && !this.isPageVisible) {
      this.focusWindow();
    }

    // 4. Play notification sound
    this.playNotificationSound();

    // 5. Show system notification as fallback
    if (!this.isEnabled()) {
      console.log(`Check-in notification: ${memberName} - ${data.validation.message}`);
      
      // As a last resort, use alert() for critical failures when tab is not visible
      if (!isSuccess && !this.isPageVisible) {
        setTimeout(() => {
          // eslint-disable-next-line no-restricted-globals
          if (confirm(`Member Check-in Alert: ${memberName} - ${data.validation.message}. View dashboard?`)) {
            window.location.hash = '#/dashboard';
          }
        }, 100);
      }
    }
  }
}

// Create and export a singleton instance
const notificationService = new NotificationService();
export default notificationService;
